import { inject, Injectable } from '@angular/core'
import { IndicativeOfferRequest } from '../models/indicative-offer-request'
import { FormArray, FormGroup } from '@angular/forms'
import {
  SolarResourceFormControls,
  SolarTechnicalResource,
  WindResourceFormControls,
  WindTechnicalResource,
} from '../models/technical-resource'
import moment from 'moment'
import { mapProductionTechnology, ProductionTechnology } from '../models/production-technology.enum'
import { GENERATION_UNIT_DATE_FORMATTER } from './market-register-data.service'
import { REGIME } from '../models/price'
import { GeneralDataRawValue, PriceFormData } from '../models/main-form'
import { normalizeRegistrationNumber } from '../utils/registration-number'
import { ProductConfigurationService } from './product-configuration.service'
import { environment } from '../../../environments/environment'

@Injectable({
  providedIn: 'root',
})
export class RequestMappingService {
  private readonly priceBalancingModel = 'Prognosemodell'
  private readonly priceCallProcedure = 'Duldungsfall'

  private readonly productConfigurationService = inject(ProductConfigurationService)

  mapToRequest(
    generalData: FormGroup,
    contactData: FormGroup,
    priceData: FormGroup,
    technicalResources: FormArray<FormGroup<SolarResourceFormControls | WindResourceFormControls>>
  ): IndicativeOfferRequest {
    const general = generalData.getRawValue()
    const contact = contactData.getRawValue()
    const price = priceData.getRawValue() as PriceFormData
    const productAttributes = this.productConfigurationService.getIndicativeOfferProductAttributes(
      general as GeneralDataRawValue
    )

    return {
      requestTimestamp: moment().utc().toISOString(),
      product: productAttributes.product,
      technicalData: {
        //general data mapping (first step, upper section)
        location_name_customer: general.locationNameCustomer ?? '',
        production_technology: mapProductionTechnology(general.productionTechnology),
        is_already_in_operation: general.isAlreadyInOperation,
        marketing_type: general.marketingType,
        contract_start_date: this.formatDate(general.contractStartDate),
        contract_end_date: this.formatDate(general.contractEndDate),

        //technical resources mapping (first step, second section)
        assets: this.mapTechnicalResourcesToAssets(
          technicalResources,
          general.productionTechnology,
          general.isAlreadyInOperation
        ),

        //general data mapping (first step, lower section)
        total_installed_capacity: general.totalInstalledCapacity,
        planned_production_per_year: Number(general.plannedProductionPerYear),
        own_consumption_percent: environment.enableSmallDV ? Number(general.ownConsumptionPercent) : 0,
        own_consumption_kwh: environment.enableSmallDV ? Number(general.ownConsumptionKw) : 0,
        feed_in_per_year: environment.enableSmallDV ? Number(general.feedInPerYear) : 0,
        battery_storage_status: general.batteryStorageStatus,
        battery_storage_usages: general.batteryStorageUsages.join(','),
      },
      contactData: {
        contract_partner: contact.contractPartner,
        contract_owner_name: contact.contractOwnerName ?? '',
        registration_type: contact.registrationType || null,
        registration_authority_name: contact.registrationAuthorityName || null,
        registration_number: normalizeRegistrationNumber(contact.registrationNumber) ?? null,
        first_name: contact.firstName ?? '',
        last_name: contact.lastName ?? '',
        address_postal_code: contact.addressPostalCode,
        address_city: contact.addressCity,
        address_street: contact.addressStreet,
        address_house_number: contact.addressHouseNumber,
        contact_info_firstName: contact.contactInfoFirstName,
        contact_info_lastName: contact.contactInfoLastName,
        contact_info_phone: contact.contactInfoPhone,
        contact_info_email: contact.contactInfoEmail,
        dataprivacy_consent: contact.dataProtectionAccepted,
      },
      ...(price.price == null || price.pricingId == null
        ? {}
        : {
            pricingData: {
              dle_capacity_price_eur_per_month: productAttributes.dleCapacityPriceEurPerMonth,
              dle_energy_price_eur_per_mwh: productAttributes.dleEnergyPriceEurPerMWh,
              service_fee_unit: price.serviceFeeUnit,
              redispatch_settlement: productAttributes.redispatchSettlement,
              balancing_model: this.priceBalancingModel,
              billing_model: productAttributes.redispatchSettlement,
              call_procedure: this.priceCallProcedure,
              unit: price.serviceFeeUnit,
              price: price.price,
              regime: REGIME,
              request_id: price.pricingId,
            },
          }),
    }
  }

  private mapCommonAssetFields(resource: SolarTechnicalResource | WindTechnicalResource, inOperation: boolean) {
    return {
      mastr_id_generation_unit: resource.mastrIdGenerationUnit ?? '',
      installed_capacity: this.parseCommaToDotNumber(resource.installedCapacityKW),
      commissioning_date: inOperation ? this.formatDate(resource.commissioningDateCommon) : '',
      commissioning_date_planned: inOperation ? '' : this.formatDate(resource.commissioningDateCommon),
      // we use cents/kwh in the UI, but the api expects eur/mwh, see https://dev.azure.com/enbw/interconnector/_workitems/edit/4401574
      applicable_value_eur_per_mwh: this.parseNullableNumberWithFactor(resource.applicableValueCentsPerkWh, 10),
      longitude: this.parseCommaToDotNumber(resource.longitude),
      latitude: this.parseCommaToDotNumber(resource.latitude),
    }
  }

  private mapTechnicalResourcesToAssets(
    resources: FormArray<FormGroup<SolarResourceFormControls | WindResourceFormControls>>,
    productionTechnology: ProductionTechnology,
    inOperation: boolean
  ): IndicativeOfferRequest['technicalData']['assets'] {
    return resources.controls.map((resource) => {
      const value = resource.value
      const isSolar = productionTechnology === ProductionTechnology.Solar

      if (isSolar) {
        const solar = value as SolarTechnicalResource
        const common = this.mapCommonAssetFields(solar, inOperation)
        return {
          ...common,
          solar_tracked_modules_bool: !!solar.solarTrackedModules,
          solar_tilt_degree: this.parseNullableNumber(solar.solarTiltDegree),
          solar_orientation: solar.solarOrientation || null,
          solar_bifacial_bool: !!solar.solarBifacial,
          // Wind specific empty fields
          turbine_manufacturer: '',
          turbine_type: '',
          turbine_hub_heigth: null,
        }
      } else {
        const wind = value as WindTechnicalResource
        const common = this.mapCommonAssetFields(wind, inOperation)
        return {
          ...common,
          turbine_manufacturer: wind.turbineManufacturer ?? '',
          turbine_type: wind.turbineType ?? '',
          turbine_hub_heigth: this.parseNullableNumber(wind.turbineHubHeight),

          // Solar specific empty fields
          solar_tracked_modules_bool: false,
          solar_tilt_degree: null,
          solar_orientation: null,
          solar_bifacial_bool: false,
        }
      }
    })
  }

  private formatDate(date: moment.Moment | Date | null): string {
    if (!date) return ''
    const dateToFormat = date instanceof Date ? moment(date) : date?.clone()
    return dateToFormat.format(GENERATION_UNIT_DATE_FORMATTER)
  }

  private parseNullableNumberWithFactor(value: string | number | null | undefined, factor: number): number | null {
    const parsed = this.parseNullableNumber(value)
    return parsed ? parsed * factor : null
  }

  private parseNullableNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null
    return this.parseCommaToDotNumber(value)
  }

  private parseCommaToDotNumber(value: string | number | null | undefined): number {
    if (typeof value === 'number') return value
    if (typeof value !== 'string') return 0

    const normalized = value.replace(',', '.')
    const parsed = parseFloat(normalized)
    return isNaN(parsed) ? 0 : parsed
  }
}
